import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Cloud } from 'lucide-react';

export default function MultiNubePieChart({ 
  distribucionData = [], 
  multiNubeStats = {},
  isDarkMode = true,
  onNubeClick = null
}) {
  const cardClasses = isDarkMode
    ? 'bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 shadow-lg'
    : 'bg-white border border-gray-200 rounded-xl p-6 shadow-lg';

  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';

  // 🎨 Mapeo fijo basado en nombre de nube
  const COLOR_MAP = {
    'Microsoft 365': '#00ffb3',     // Verde cyan
    'Microsoft Azure': '#04d9ff',   // Cyan
    'Amazon AWS': '#feca57',        // Amarillo cálido
    'Google GCP': '#667eea',        // Morado
    'Google Workspace': '#f093fb',  // Rosa
    'Huawei Cloud': '#ff6b6b',      // Rojo
    'Ninguna': '#95afc0',           // Gris azul
    'default': '#4ecdc4'            // Turquesa
  };

  // Preparar datos
  const pieData = distribucionData.map((item) => ({
    name: item.nube_servicio,
    value: item.total_clientes,
    contratos: item.total_contratos,
    color: COLOR_MAP[item.nube_servicio] || COLOR_MAP.default
  }));

  const clientesPorNube = pieData.reduce((sum, item) => sum + item.value, 0);
  const totalNubes = pieData.length;
  const totalMultiNube = multiNubeStats.total_multi_nube || 0;
  const porcentajeMultiNube = multiNubeStats.porcentaje_multi_nube || 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const total = pieData.reduce((sum, item) => sum + item.value, 0);
      const percent = total > 0 ? ((data.value / total) * 100).toFixed(1) : 0;
      
      return (
        <div className={`${
          isDarkMode 
            ? 'bg-gray-900/95 border-2 border-white/30' 
            : 'bg-white/95 border-2 border-gray-300'
        } backdrop-blur-md rounded-lg p-3 shadow-xl`}>
          <p className={`${textPrimary} font-bold mb-2 text-base`}>{data.name}</p>
          <p className={`${textSecondary} text-sm mb-1`}>
            Clientes: <span className={`${textPrimary} font-semibold`}>{data.value}</span>
          </p>
          <p className={`${textSecondary} text-sm`}>
            Porcentaje: <span className={`${textPrimary} font-semibold`}>{percent}%</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
          <Cloud className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className={`${textPrimary} text-xl font-bold`}>
            Distribución de Clientes por Nube
          </h3>
          <p className={`${textSecondary} text-sm`}>
            {totalMultiNube} clientes con múltiples nubes ({porcentajeMultiNube}%)
          </p>
        </div>
      </div>

      {/* Gráfica */}
      {pieData.length === 0 ? (
        <div className={`${textSecondary} text-center py-8`}>
          No hay datos disponibles
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
                onClick={(data) => onNubeClick && onNubeClick(data.name)}
                style={{ cursor: onNubeClick ? 'pointer' : 'default' }}
              >
                {pieData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    className="transition-opacity hover:opacity-80"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          {/* TARJETAS DE NUBES */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {pieData.map((nube, index) => (
              <div
                key={index}
                className={`${
                  isDarkMode ? 'bg-white/5' : 'bg-gray-50'
                } rounded-lg p-3 flex items-center gap-2 cursor-pointer hover:bg-white/10 transition-all`}
                onClick={() => onNubeClick && onNubeClick(nube.name)}
              >
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: nube.color }}
                />
                <div className="flex-1 min-w-0">
                  <p className={`${textPrimary} font-semibold text-sm truncate`}>
                    {nube.name}
                  </p>
                  <p className={`${textSecondary} text-m`}>
                    {nube.value}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Footer con estadísticas */}
          <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Clientes por Nube</p>
                <p className={`${textPrimary} text-2xl font-bold`}>
                  {clientesPorNube}
                </p>
                <p className={`${textSecondary} text-[10px]`}>(suma por nube)</p>
              </div>
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Multi-Nube</p>
                <p className={`${textPrimary} text-2xl font-bold text-cyan-400`}>
                  {totalMultiNube}
                </p>
                <p className={`${textSecondary} text-[10px]`}>({porcentajeMultiNube}%)</p>
              </div>
              <div>
                <p className={`${textSecondary} text-xs mb-1 uppercase`}>Total Nubes</p>
                <p className={`${textPrimary} text-2xl font-bold text-purple-400`}>
                  {totalNubes}
                </p>
                <p className={`${textSecondary} text-[10px]`}>(nubes activas)</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
